import React, { Fragment, useEffect } from 'react';
import {
  Box,
  SolidDivider,
  Stack,
  Cluster,
  Paragraph,
  ButtonWithAction,
  constants,
  util,
  FormContainer,
  Checkbox,
  FormInput,
} from '@thecb/components';
import { required, isProbablyEmail, hasLength } from 'redux-freeform';
import {
  reducer,
  mapStateToProps,
  mapDispatchToProps,
} from './ReceiptForm.state';
import { OrderReceiptReportDocument } from '../../components/pdf-reports';
import { useSelector } from 'react-redux';
import { pdf } from '@react-pdf/renderer';
import { downloadReport, getImageOrientation } from '../../utils/helpers';
import fallbackLogo from '../../static/img/cityville-logo-blue.png';
import {
  EMAIL_ADDRESS_FIELD_NAME,
  PHONE_NUMBER_FIELD_NAME,
} from '../../utils/constants';

const ReceiptFormModule = ({
  actions,
  buttonStyle,
  forms,
  handleErrors,
  showErrors,
  submitSendReceipt,
  submitPrintReceipt,
  cancelAction,
}) => {
  const {
    clientMetadata: { data: metadata },
  } = useSelector((state) => state.global);
  const { data } = useSelector((state) => state.orderDetails);
  const { terminal } = useSelector(
    (state) => state.terminalLogin.terminalSession || '',
  );
  const {
    data: selectedOrder,
    isPrintReceiptLoading,
    isSendReceiptLoading,
  } = useSelector((state) => state.orderDetails);
  const lazyLoadPdf = async () => {
    const portraitOrLandscapeLogo = await getImageOrientation(
      metadata?.headerLogoLink ?? fallbackLogo, // in case FCS is not running locally
    );
    // from https://github.com/diegomura/react-pdf/issues/975
    const doc = (
      <OrderReceiptReportDocument
        headerImg={metadata?.headerLogoLink}
        portraitOrLandscapeLogo={portraitOrLandscapeLogo}
        {...data}
      />
    );
    const asPdf = pdf();
    asPdf.updateContainer(doc);
    const blob = await asPdf.toBlob();
    const objectUrl = window.URL.createObjectURL(blob);
    downloadReport(
      `Order-ID-${selectedOrder.order.id}-Receipt`,
      terminal,
      new Date(),
      objectUrl,
    );
  };

  const handleSendReceiptClick = util.general.generateClickHandler(
    forms.receiptForm,
    () => handleErrors(true),
    () => submitSendReceipt(),
  );
  const disableSendReceiptButton = !(
    forms.receiptForm.fields.textReceipt.rawValue ||
    forms.receiptForm.fields.emailReceipt.rawValue
  );
  return (
    <Fragment>
      <SolidDivider />
      <Box background={constants.colors.WHITE}>
        <Cluster justify="stretch">
          <Stack width="100%" extraStyles={`flex-grow: 1;`}>
            <Paragraph extraStyles={`max-width: 32rem`}>
              Please enter the email address or phone number to send a copy of
              this receipt or click “Print receipt” to print a receipt.
            </Paragraph>
            <ReceiptForm
              showErrors={showErrors}
              {...forms.receiptForm}
              {...actions.forms.receiptForm}
            />
          </Stack>
        </Cluster>
      </Box>
      <Box background={constants.colors.WHITE}>
        <Cluster justify="stretch">
          <Cluster
            justify="flex-end"
            extraStyles={`flex-grow: 1; align-self: flex-end;`}
          >
            <ButtonWithAction
              extraStyles={buttonStyle}
              textExtraStyles="font-size: 1rem;"
              text="Cancel"
              isLoading={false}
              variant="secondary"
              action={cancelAction}
            />
            <ButtonWithAction
              id="print-receipt-btn"
              extraStyles={buttonStyle}
              textExtraStyles="font-size: 1rem;"
              text="Print Receipt"
              isLoading={isPrintReceiptLoading}
              disabled={isPrintReceiptLoading}
              action={submitPrintReceipt}
            />
            <ButtonWithAction
              id="pdf-receipt-btn"
              extraStyles={buttonStyle}
              textExtraStyles="font-size: 1rem;"
              text="PDF Receipt"
              isLoading={false}
              action={() => lazyLoadPdf()}
            />
            <ButtonWithAction
              id="send-receipt-btn"
              disabled={disableSendReceiptButton || isSendReceiptLoading}
              extraStyles={buttonStyle}
              textExtraStyles="font-size: 1rem;"
              text="Send Receipt"
              isLoading={isSendReceiptLoading}
              action={handleSendReceiptClick}
            />
          </Cluster>
        </Cluster>
      </Box>
    </Fragment>
  );
};

const emailReceiptErrors = {
  [required.error]: 'Email address required',
  [isProbablyEmail.error]: 'Not a valid email address',
};
const textReceiptErrors = {
  [required.error]: 'Phone number required',
  [hasLength.error]: '10 digit phone number required',
};

const ReceiptForm = ({ actions, fields, showErrors }) => {
  useEffect(() => {
    return () => actions.form.clear();
  }, []);
  const currentEmailReceiptBoolean = Boolean(fields.emailReceipt.rawValue);
  const resetFieldValueOnSelect = (field) => {
    if (field && fields[field]?.rawValue) {
      actions.fields[field].set('');
    }
  };
  const handleEmailReceiptCheckboxClick = () => {
    actions.fields.emailReceipt.set(!currentEmailReceiptBoolean);
    resetFieldValueOnSelect(EMAIL_ADDRESS_FIELD_NAME);
  };
  const currentTextReceiptBoolean = Boolean(fields.textReceipt.rawValue);
  const handleTextReceiptCheckboxClick = () => {
    actions.fields.textReceipt.set(!currentTextReceiptBoolean);
    resetFieldValueOnSelect(PHONE_NUMBER_FIELD_NAME);
  };

  return (
    <FormContainer padding="0 0 0 1rem">
      <Checkbox
        id={'email-receipt'}
        title={'Email Receipt'}
        name={'Email Receipt'}
        checked={currentEmailReceiptBoolean}
        onChange={handleEmailReceiptCheckboxClick}
      />
      {currentEmailReceiptBoolean && (
        <FormInput
          id="email-address-input"
          labelTextWhenNoError="Email Address"
          errorMessages={emailReceiptErrors}
          field={fields.emailAddress}
          errors={fields.errors}
          showErrors={showErrors}
          fieldActions={actions.fields.emailAddress}
          labelTextVariant="p"
          errorTextVariant="p"
        />
      )}
      <Checkbox
        id={'text-receipt'}
        title={'Text Receipt'}
        name={'Text Receipt'}
        checked={currentTextReceiptBoolean}
        onChange={handleTextReceiptCheckboxClick}
      />
      {currentTextReceiptBoolean && (
        <FormInput
          id="phone-number-input"
          labelTextWhenNoError="Phone Number"
          errorMessages={textReceiptErrors}
          field={fields.phoneNumber}
          errors={fields.errors}
          showErrors={showErrors}
          fieldActions={actions.fields.phoneNumber}
          labelTextVariant="p"
          errorTextVariant="p"
        />
      )}
    </FormContainer>
  );
};

ReceiptForm.reducer = reducer;
ReceiptForm.mapStateToProps = mapStateToProps;
ReceiptForm.mapDispatchToProps = mapDispatchToProps;

export { ReceiptForm, ReceiptFormModule };
